import {
  differenceInYears,
  isInFuture,
  isInPast,
  parseIsoDate,
} from '../../utils/dateUtil';
import { useCommonTranslation } from '../i18n/useCommonTranslation';
import { useMemo } from 'react';
import {
  hookFromTrimBeforeValidation,
  NAME_PATTERN,
  optionalPhoneValidate,
  singleEmailPattern,
} from '../../utils/helpers';
import { useRole } from '../../context/RoleContext';
import { PATIENT } from '../../configs/constants';
import { useAuth } from '../../context/AuthContext';

export const useUserValidationRules = ({
  emailRequired = false,
}: {
  emailRequired?: boolean;
} = {}) => {
  const { t } = useCommonTranslation();
  const { sangixUser } = useAuth();
  const { hasAnyOfRolesInAllSites } = useRole();
  const phoneRequiredOrNotRule = useMemo(
    () =>
      !sangixUser || hasAnyOfRolesInAllSites(PATIENT)
        ? {
            required: t('must-not-empty') as string,
          }
        : {},
    [hasAnyOfRolesInAllSites, sangixUser, t],
  );

  return useMemo(() => {
    return {
      birthdateRules: {
        ...hookFromTrimBeforeValidation,
        required: t<string>('must-not-empty'),
        validate: (value?: string) => {
          if (!value) return t('must-not-empty');
          const date = parseIsoDate(value);
          if (isInFuture(date)) return t('birthday-validation-before');
          if (isInPast(date) && differenceInYears(new Date(), date) > 150) {
            return t('birthday-validation-maxAge', {
              age: 150,
            });
          }
        },
      },
      firstnameRules: {
        ...hookFromTrimBeforeValidation,
        required: t('must-not-empty') as string,
        pattern: {
          value: NAME_PATTERN,
          message: t('must-valid-name') as string,
        },
      },
      surnameRules: {
        ...hookFromTrimBeforeValidation,
        required: t('must-not-empty') as string,
        pattern: {
          value: NAME_PATTERN,
          message: t('must-valid-name') as string,
        },
      },
      emailRules: {
        ...hookFromTrimBeforeValidation,
        required: emailRequired ? (t('must-not-empty') as string) : undefined,
        pattern: {
          value: singleEmailPattern,
          message: t('must-valid-address') as string,
        },
      },
      phoneRules: {
        ...phoneRequiredOrNotRule,
        validate: (value?: string) => optionalPhoneValidate(t, value),
      },
      commentRules: {
        ...hookFromTrimBeforeValidation,
        maxLength: {
          value: 10240,
          message: t('max-char-input', { count: 10240 }),
        },
      },
    };
  }, [t, emailRequired, phoneRequiredOrNotRule]);
};
